import RouteGuard from '@/components/RouteGuard';
import ClientLayout from '@/components/ClientLayout';

import { RootState } from '@/lib/store';
import { lazy, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RouteObject } from 'react-router-dom';
import { setItemWithExpiry } from '@/utils/helpers/storage';

const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const AccessDenied = lazy(() => import('@/pages/AccessDenied'));

const Permissions = lazy(() => import('@/pages/Permissions'));
const PermissionForm = lazy(() => import('@/pages/Permissions/PermissionForm'));
const PermissionDetail = lazy(
  () => import('@/pages/Permissions/PermissionDetail')
);

const PageRoutes = lazy(() => import('@/pages/PageRoutes'));
const PageDetailRoute = lazy(
  () => import('@/pages/PageRoutes/PageRouteDetail')
);
const PageRouteForm = lazy(() => import('@/pages/PageRoutes/PageRouteForm'));
const Roles = lazy(() => import('@/pages/Roles'));
const RoleForm = lazy(() => import('@/pages/Roles/RoleForm'));

const RoleDetail = lazy(() => import('@/pages/Roles/RoleDetail'));

const BillingForm = lazy(() => import('@/components/billing/BillingForm'));

const Products = lazy(() => import('@/pages/Products'));
const ProductForm = lazy(() => import('@/pages/Products/ProductForm'));
const ProductDetail = lazy(() => import('@/pages/Products/ProductDetail'));
const PriceDetail = lazy(() => import('@/pages/Products/PriceDetail'));

const Marketplaces = lazy(() => import('@/pages/MarketPlaces'));
const MarketPlaceForm = lazy(
  () => import('@/pages/MarketPlaces/MarketPlaceForm')
);
const MarketPlaceDetail = lazy(
  () => import('@/pages/MarketPlaces/MarketPlaceDetail')
);

const Consents = lazy(() => import('@/pages/Consents'));

const Subscription = lazy(() => import('@/pages/Subscription'));
const Region = lazy(() => import('@/pages/Region'));
const RegionForm = lazy(() => import('@/pages/Region/RegionForm'));

const Vendor = lazy(() => import('@/pages/Vendor'));
const VendorForm = lazy(() => import('@/pages/Vendor/VendorForm'));
const VendorDetail = lazy(() => import('@/pages/Vendor/VendorDetail'));

const Support = lazy(() => import('@/pages/Support'));
const SupportForm = lazy(() => import('@/pages/Support/SupportForm'));
const SupportDetail = lazy(() => import('@/pages/Support/SupportDetail'));
const Messages = lazy(() => import('@/pages/Support/Messages'));
const MessageForm = lazy(() => import('@/pages/Support/Messages/MessageForm'));
const MessageDetail = lazy(
  () => import('@/pages/Support/Messages/MessageDetail')
);

const UserManagement = lazy(() => import('@/pages/UserManagment'));
const UserDetail = lazy(() => import('@/pages/UserManagment/UserDetail'));
const UserForm = lazy(() => import('@/pages/UserManagment/UserForm'));

const Catalogs = lazy(() => import('@/pages/Catalogs'));
const CatalogDetail = lazy(() => import('@/pages/Catalogs/CatalogDetail'));

const Dashboard = lazy(() => import('@/pages/Dashboard'));

const Affiliate = lazy(() => import('@/pages/Affiliate'));
const AffiliateForm = lazy(() => import('@/pages/Affiliate/AffiliateForm'));
const AffiliateDetail = lazy(
  () => import('@/pages/Affiliate/AffiliateDetail')
);
const AffiliateAllCommissions = lazy(
  () => import('@/pages/AffiliateAllCommissions')
);
const AffiliateDashboard = lazy(() => import('@/pages/AffiliateDashboard'));

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Root seviyesinde OAuth parametrelerini kontrol eden component
const RootLayout: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Senaryo 1: Amazon ürün yetkilendirme başlatma
    const amazonCallbackUri = params.get('amazon_callback_uri');
    const amazonState = params.get('amazon_state');
    const sellingPartnerId = params.get('selling_partner_id');

    // Senaryo 2: Amazon callback validate
    const state = params.get('state');
    const spapiOauthCode = params.get('spapi_oauth_code');
    const validateSellingPartnerId = params.get('selling_partner_id');

    // Senaryo 1: Amazon callback params kaydet
    if (amazonCallbackUri && amazonState && sellingPartnerId) {
      setItemWithExpiry('pendingAmazonCallbackParams', {
        amazonCallbackUri,
        amazonState,
        sellingPartnerId,
      });
    }

    // Senaryo 2: Amazon validate params kaydet
    if (state && validateSellingPartnerId && spapiOauthCode) {
      setItemWithExpiry('pendingAmazonValidateParams', {
        state,
        selling_partner_id: validateSellingPartnerId,
        spapi_oauth_code: spapiOauthCode,
      });
    }
  }, [location.search]);

  return <Outlet />;
};

const ProtectedLayout: React.FC = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <RouteGuard>
      <ClientLayout>
        <Outlet />
      </ClientLayout>
    </RouteGuard>
  );
};

export const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
];

export const protectedRoutes: RouteObject[] = [
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        path: 'dashboard',
        element: <Dashboard />,
        handle: { title: 'routes.dashboard' },
      },
      {
        path: 'page-routes',
        handle: { title: 'routes.pageRoutes.title' },
        children: [
          {
            index: true,
            element: <PageRoutes />,
          },
          {
            path: 'create',
            element: <PageRouteForm />,
            handle: { title: 'routes.pageRoutes.create' },
          },
          {
            path: ':id/edit',
            element: <PageRouteForm />,
            handle: { title: 'routes.pageRoutes.edit' },
          },
          {
            path: ':id',
            element: <PageDetailRoute />,
            handle: { title: 'routes.pageRoutes.detail' },
          },
        ],
      },
      {
        path: 'page-route-permissions',
        children: [
          {
            index: true,
            element: <Permissions />,
            handle: { title: 'routes.pageRoutePermissions.title' },
          },
          {
            path: 'create',
            element: <PermissionForm />,
            handle: { title: 'routes.pageRoutePermissions.create' },
          },
          {
            path: ':id/edit',
            element: <PermissionForm />,
            handle: { title: 'routes.pageRoutePermissions.edit' },
          },
          {
            path: ':id',
            element: <PermissionDetail />,
            handle: { title: 'routes.pageRoutePermissions.detail' },
          },
        ],
      },
      {
        path: 'roles',
        handle: { title: 'routes.roles.title' },
        children: [
          {
            index: true,
            element: <Roles />,
          },
          {
            path: 'create',
            element: <RoleForm />,
            handle: { title: 'routes.roles.create' },
          },
          {
            path: ':id/edit',
            element: <RoleForm />,
            handle: { title: 'routes.roles.edit' },
          },
          {
            path: ':id',
            element: <RoleDetail />,
            handle: { title: 'routes.roles.detail' },
          },
        ],
      },
      {
        path: 'products',
        handle: { title: 'routes.products.title' },
        children: [
          {
            index: true,
            element: <Products />,
          },
          {
            path: 'create',
            element: <ProductForm />,
            handle: { title: 'routes.products.create' },
          },
          {
            path: ':id',
            element: <ProductDetail />,
            handle: { title: 'routes.products.detail' },
          },
          {
            path: ':id/edit',
            element: <ProductForm />,
            handle: { title: 'routes.products.edit' },
          },
          {
            path: ':productId/prices/:priceId',
            element: <PriceDetail />,
            handle: { title: 'routes.products.priceDetail' },
          },
        ],
      },
      {
        path: 'billings',
        element: <BillingForm />,
        handle: { title: 'routes.billings.title' },
      },
      {
        path: 'marketplaces',
        handle: { title: 'routes.marketplaces.title' },
        children: [
          {
            index: true,
            element: <Marketplaces />,
          },
          {
            path: 'create',
            element: <MarketPlaceForm />,
            handle: { title: 'routes.marketplaces.create' },
          },
          {
            path: ':id',
            element: <MarketPlaceDetail />,
            handle: { title: 'routes.marketplaces.detail' },
          },
          {
            path: ':id/edit',
            element: <MarketPlaceForm />,
            handle: { title: 'routes.marketplaces.edit' },
          },
        ],
      },
      {
        path: 'consents',
        element: <Consents />,
        handle: { title: 'routes.consents.title' },
      },
      {
        path: 'subscription',
        element: <Subscription />,
        handle: { title: 'routes.subscription.title' },
      },
      {
        path: 'regions',
        handle: { title: 'routes.regions.title' },
        children: [
          {
            index: true,
            element: <Region />,
          },
          {
            path: 'create',
            element: <RegionForm />,
            handle: { title: 'routes.regions.create' },
          },
          {
            path: ':id/edit',
            element: <RegionForm />,
            handle: { title: 'routes.regions.edit' },
          },
        ],
      },
      {
        path: 'vendors',
        handle: { title: 'routes.vendors.title' },
        children: [
          {
            index: true,
            element: <Vendor />,
          },
          {
            path: 'create',
            element: <VendorForm />,
            handle: { title: 'routes.vendors.create' },
          },
          {
            path: ':id',
            element: <VendorDetail />,
            handle: { title: 'routes.vendors.detail' },
          },
          {
            path: ':id/edit',
            element: <VendorForm />,
            handle: { title: 'routes.vendors.edit' },
          },
        ],
      },
      {
        path: 'user-management',
        handle: { title: 'routes.userManagement.title' },
        children: [
          {
            index: true,
            element: <UserManagement />,
          },
          {
            path: ':id',
            element: <UserDetail />,
            handle: { title: 'routes.userManagement.detail' },
          },
          {
            path: 'create',
            element: <UserForm />,
            handle: { title: 'routes.userManagement.create' },
          },
          {
            path: ':id/edit',
            element: <UserForm />,
            handle: { title: 'routes.userManagement.edit' },
          },
        ],
      },
      {
        path: 'support',
        handle: { title: 'routes.support.title' },
        children: [
          {
            index: true,
            element: <Support />,
          },
          {
            path: 'create',
            element: <SupportForm />,
            handle: { title: 'routes.support.create' },
          },
          {
            path: ':id/edit',
            element: <SupportForm />,
            handle: { title: 'routes.support.edit' },
          },
          {
            path: ':id',
            element: <SupportDetail />,
            handle: { title: 'routes.support.detail' },
          },
          {
            path: ':id/messages',
            handle: { title: 'routes.support.messages.title' },
            children: [
              {
                index: true,
                element: <Messages />,
              },
              {
                path: 'create',
                element: <MessageForm />,
                handle: { title: 'routes.support.messages.create' },
              },
              {
                path: ':messageId/edit',
                element: <MessageForm />,
                handle: { title: 'routes.support.messages.edit' },
              },
              {
                path: ':messageId',
                element: <MessageDetail />,
                handle: { title: 'routes.support.messages.detail' },
              },
            ],
          },
        ],
      },
      {
        path: 'catalogs',
        handle: { title: 'routes.catalogs.title' },
        children: [
          {
            index: true,
            element: <Catalogs />,
          },
          {
            path: ':id',
            element: <CatalogDetail />,
            handle: { title: 'routes.catalogs.detail' },
          },
        ],
      },
      {
        path: 'affiliates',
        handle: { title: 'routes.affiliates.title' },
        children: [
          {
            index: true,
            element: <Affiliate />,
          },
          {
            path: 'create',
            element: <AffiliateForm />,
            handle: { title: 'routes.affiliates.create' },
          },
          {
            path: ':id',
            element: <AffiliateDetail />,
            handle: { title: 'routes.affiliates.detail' },
          },
          {
            path: ':id/edit',
            element: <AffiliateForm />,
            handle: { title: 'routes.affiliates.edit' },
          },
        ],
      },
      {
        path: 'affiliate-commissions',
        element: <AffiliateAllCommissions />,
        handle: { title: 'routes.affiliateCommissions.title' },
      },
      {
        path: 'affiliate-dashboard',
        element: <AffiliateDashboard />,
        handle: { title: 'routes.affiliateDashboard.title' },
      },
    ],
  },
];

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '*',
        element: <AccessDenied />,
      },
      ...publicRoutes,
      ...protectedRoutes,
    ],
  },
];
